/**
 * 移动端自适应解决方案
 * 基于 viewport 和 rem 的混合方案
 */

// 设计稿尺寸配置
const DESIGN_WIDTH = 375
const DESIGN_HEIGHT = 667

// 设备信息
interface DeviceInfo {
  width: number
  height: number
  dpr: number
  scale: number
  isLandscape: boolean
}

class FlexibleAdapter {
  private deviceInfo: DeviceInfo
  private rootElement: HTMLElement

  constructor() {
    this.rootElement = document.documentElement
    this.deviceInfo = this.getDeviceInfo()
    this.init()
  }

  // 获取设备信息
  private getDeviceInfo(): DeviceInfo {
    const { innerWidth, innerHeight, devicePixelRatio } = window
    const dpr = devicePixelRatio || 1

    return {
      width: innerWidth,
      height: innerHeight,
      dpr,
      scale: 1 / dpr,
      isLandscape: innerWidth > innerHeight
    }
  }

  // 初始化
  private init() {
    this.setViewport()
    this.setRootFontSize()
    this.setSafeArea()
    this.setCustomProperties()
    this.bindEvents()

    // 设置完成后触发自定义事件
    this.dispatchReadyEvent()
    
  }

  // 设置 viewport
  private setViewport() {
    let viewportContent =
      'width=device-width, initial-scale=1.0, maximum-scale=1.0, minimum-scale=1.0, user-scalable=no, viewport-fit=cover'

    // 针对特定设备优化
    if (this.isIOS()) {
      viewportContent += ', shrink-to-fit=no'
    }

    this.setMetaViewport(viewportContent)
  }

  // 设置或更新 viewport meta 标签
  private setMetaViewport(content: string) {
    let viewport = document.querySelector('meta[name="viewport"]') as HTMLMetaElement

    if (!viewport) {
      viewport = document.createElement('meta')
      viewport.name = 'viewport'
      document.head.appendChild(viewport)
    }

    viewport.content = content
  }

  // 设置根字体大小 (rem 基准)
  private setRootFontSize() {
    const { width } = this.deviceInfo
    // 基于设计稿宽度计算 rem 基准值
    const fontSize = (width / DESIGN_WIDTH) * 16 // 16px 为基准

    // 限制字体大小范围
    const minFontSize = 12
    const maxFontSize = 24
    const finalFontSize = Math.max(minFontSize, Math.min(maxFontSize, fontSize))

    this.rootElement.style.fontSize = `${finalFontSize}px`
  }

  // 设置安全区域变量
  private setSafeArea() {
    // iOS 刘海屏适配
    if (this.isIOS() && this.hasNotch()) {
      this.rootElement.style.setProperty('--safe-area-inset-top', 'env(safe-area-inset-top)')
      this.rootElement.style.setProperty('--safe-area-inset-bottom', 'env(safe-area-inset-bottom)')
      this.rootElement.style.setProperty('--safe-area-inset-left', 'env(safe-area-inset-left)')
      this.rootElement.style.setProperty('--safe-area-inset-right', 'env(safe-area-inset-right)')
    } else {
      // 其他设备默认值
      this.rootElement.style.setProperty('--safe-area-inset-top', '0px')
      this.rootElement.style.setProperty('--safe-area-inset-bottom', '0px')
      this.rootElement.style.setProperty('--safe-area-inset-left', '0px')
      this.rootElement.style.setProperty('--safe-area-inset-right', '0px')
    }
  }

  // 设置自定义CSS属性
  private setCustomProperties() {
    const { width, height, dpr, isLandscape } = this.deviceInfo

    this.rootElement.style.setProperty('--screen-width', `${width}px`)
    this.rootElement.style.setProperty('--screen-height', `${height}px`)
    this.rootElement.style.setProperty('--device-pixel-ratio', dpr.toString())
    this.rootElement.style.setProperty('--is-landscape', isLandscape ? '1' : '0')

    // 1px 边框解决方案
    this.rootElement.style.setProperty('--hairline-width', `${1 / dpr}px`)

    // 状态栏高度 (估算)
    const statusBarHeight = this.getStatusBarHeight()
    this.rootElement.style.setProperty('--status-bar-height', `${statusBarHeight}px`)

    // 底部导航栏高度
    const bottomBarHeight = this.getBottomBarHeight()
    this.rootElement.style.setProperty('--bottom-bar-height', `${bottomBarHeight}px`)
  }

  // 绑定事件监听
  private bindEvents() {
    // 监听窗口大小变化
    window.addEventListener('resize', this.handleResize.bind(this), { passive: true })

    // 监听设备方向变化
    window.addEventListener('orientationchange', this.handleOrientationChange.bind(this), {
      passive: true
    })

    // 监听页面可见性变化
    document.addEventListener('visibilitychange', this.handleVisibilityChange.bind(this), {
      passive: true
    })
  }

  // 处理窗口大小变化
  private handleResize() {
    // 防抖处理
    clearTimeout(this.resizeTimer)
    this.resizeTimer = setTimeout(() => {
      this.deviceInfo = this.getDeviceInfo()
      this.setRootFontSize()
      this.setCustomProperties()
      this.dispatchResizeEvent()
    }, 150)
  }

  private resizeTimer: number = 0

  // 处理设备方向变化
  private handleOrientationChange() {
    setTimeout(() => {
      this.deviceInfo = this.getDeviceInfo()
      this.setRootFontSize()
      this.setCustomProperties()
      this.dispatchOrientationChangeEvent()
    }, 300) // 等待方向变化完成
  }

  // 处理页面可见性变化
  private handleVisibilityChange() {
    if (!document.hidden) {
      // 页面重新可见时，重新计算尺寸
      setTimeout(() => {
        this.handleResize()
      }, 100)
    }
  }

  // 获取状态栏高度
  private getStatusBarHeight(): number {
    if (this.isIOS()) {
      // iPhone X 系列
      if (this.hasNotch()) {
        return this.deviceInfo.isLandscape ? 0 : 44
      }
      // 普通 iPhone
      return this.deviceInfo.isLandscape ? 0 : 20
    }

    // Android 设备
    return this.deviceInfo.isLandscape ? 0 : 24
  }

  // 获取底部导航栏高度
  private getBottomBarHeight(): number {
    if (this.isIOS() && this.hasNotch()) {
      return this.deviceInfo.isLandscape ? 21 : 34
    }
    return 0
  }

  // 检测是否为 iOS
  private isIOS(): boolean {
    return /iPad|iPhone|iPod/.test(navigator.userAgent)
  }

  // 检测是否有刘海屏
  private hasNotch(): boolean {
    if (!this.isIOS()) return false

    // 通过 CSS 支持检测
    if (CSS.supports('padding: max(0px)')) {
      return true
    }

    // 通过屏幕尺寸判断
    const { width, height } = this.deviceInfo
    const ratio = Math.max(width, height) / Math.min(width, height)

    return ratio > 2.1 // 刘海屏设备的屏幕比例通常大于 2.1
  }

  // 派发准备完成事件
  private dispatchReadyEvent() {
    const event = new CustomEvent('flexibleReady', {
      detail: this.deviceInfo
    })
    window.dispatchEvent(event)
  }

  // 派发窗口大小变化事件
  private dispatchResizeEvent() {
    const event = new CustomEvent('flexibleResize', {
      detail: this.deviceInfo
    })
    window.dispatchEvent(event)
  }

  // 派发方向变化事件
  private dispatchOrientationChangeEvent() {
    const event = new CustomEvent('flexibleOrientationChange', {
      detail: this.deviceInfo
    })
    window.dispatchEvent(event)
  }

  // 获取当前设备信息
  public getDeviceInfo2(): DeviceInfo {
    return { ...this.deviceInfo }
  }

  // px 转 rem
  public px2rem(px: number): string {
    const fontSize = parseFloat(this.rootElement.style.fontSize) || 16
    return `${px / fontSize}rem`
  }

  // rem 转 px
  public rem2px(rem: number): number {
    const fontSize = parseFloat(this.rootElement.style.fontSize) || 16
    return rem * fontSize
  }

  // vw 转 px
  public vw2px(vw: number): number {
    return (vw * this.deviceInfo.width) / 100
  }

  // px 转 vw
  public px2vw(px: number): string {
    return `${(px / this.deviceInfo.width) * 100}vw`
  }
}

// 创建全局实例
const flexible = new FlexibleAdapter()

// 导出工具函数
export { flexible }

// 在全局对象上挂载工具函数 (可选)
if (typeof window !== 'undefined') {
  ;(window as any).flexible = flexible
}

export default flexible
